/**
 * BundleCover — mini "book spine" cover thumbnail.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/library.jsx — BundleCover function.
 * Literal replica of the iPad design: gradient derived from bundle code hash,
 * spine lines (dark + light), serif title strip, logo dot at bottom-right.
 *
 * Styling: Tailwind v4 DS-native utilities where possible.
 * EXCEPTION — dynamic values: the gradient background and its derived hue
 * rotation (hashCode % 60), the boxShadow with brand-specific rgba values,
 * and pixel-computed sizes driven by the `large` prop cannot be expressed as
 * static Tailwind utilities. Those remain as `style={{}}` inline. All other
 * properties use className.
 */

import * as React from 'react'

// ---------------------------------------------------------------------------
// Hash helper — mirrors the design: sum of char codes mod 60
// ---------------------------------------------------------------------------

function hashCode(code: string): number {
  return code.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 60
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BundleCoverProps {
  /** Bundle code — drives the gradient hue rotation. */
  code: string
  /** Bundle title — first two words shown in the spine strip. */
  title: string
  /** Large variant (72×96) vs small (52×70). Default: false (small). */
  large?: boolean
}

// ---------------------------------------------------------------------------
// BundleCover
// ---------------------------------------------------------------------------

/**
 * Renders a coloured book-spine thumbnail. Dimensions:
 *   small (default): 52 × 70 px
 *   large:           72 × 96 px
 *
 * The gradient hue shift is derived from the bundle code so each bundle gets
 * a distinct colour family, matching the iPad design exactly.
 */
export function BundleCover({ code, title, large = false }: BundleCoverProps) {
  const w = large ? 72 : 52
  const h = large ? 96 : 70
  const h1 = hashCode(code)

  const titleWords = title.split(' ').slice(0, 2).join(' ')
  /* design handoff — title font size: 11px (large) / 9px (small), no DS token */
  const titleFontSize = large ? 11 : 9
  /* design handoff — logo dot size: 14px (large) / 10px (small), no DS token */
  const logoDot = large ? 14 : 10

  /* rounded-sm = 4px; dynamic width/height and gradient background remain inline */
  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-sm"
      style={{
        width: w,
        height: h,
        /* DYNAMIC gradient: hue derived from hashCode(code) % 60 — cannot be a static utility */
        background: `linear-gradient(135deg,
          color-mix(in oklab, var(--ar-accent) ${30 + h1 / 2}%, var(--surface-2)),
          color-mix(in oklab, var(--ar-accent) ${50 + h1}%, var(--ink-2)))`,
        /* brand boxShadow with rgba values not covered by DS shadow tokens */
        boxShadow: 'inset -2px 0 4px rgba(0,0,0,0.2), 0 2px 6px rgba(60,40,20,0.15)',
      }}
    >
      {/* Spine lines — dark then light, left edge */}
      {/* rgba background values not expressible as DS tokens — remain inline */}
      <div
        className="absolute top-0 bottom-0 left-1 w-px"
        style={{ background: 'rgba(0,0,0,0.18)' }}
      />
      <div
        className="absolute top-0 bottom-0 left-1.5 w-px"
        style={{ background: 'rgba(255,255,255,0.12)' }}
      />

      {/* Title strip — serif, white, top of spine */}
      {/* fontSize is dynamic (prop-driven); color/textShadow use rgba — remain inline */}
      <div
        className="absolute left-[10px] top-2 right-1 font-serif font-semibold leading-[1.1] tracking-[-0.01em]"
        style={{
          fontSize: titleFontSize,
          color: 'rgba(255,255,255,0.92)',
          textShadow: '0 1px 2px rgba(0,0,0,0.25)',
        }}
      >
        {titleWords}
      </div>

      {/* Logo dot — circle + inner dot, bottom-right */}
      {/* width/height are dynamic (prop-driven logoDot) — remain inline */}
      <div
        className="absolute bottom-1.5 right-1.5 flex items-center justify-center rounded-full border border-white/60"
        style={{
          width: logoDot,
          height: logoDot,
        }}
      >
        {/* rgba background — remains inline */}
        <div className="size-[3px] rounded-full" style={{ background: 'rgba(255,255,255,0.85)' }} />
      </div>
    </div>
  )
}
